import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Icon, LearnMoreLink, Text } from '@ui/themed';
import type { ReactNode } from 'react';

/**
 * One row of a settings list: label and description on the left, the control on the right.
 * Pass `children` instead to lay the row body out yourself.
 */
export function SettingRow({
  label,
  description,
  learnMoreUrl,
  control,
  children,
  tooltip,
  mono,
  truncate,
  align = 'start',
  nested,
}: {
  label?: ReactNode;
  description?: ReactNode;
  learnMoreUrl?: string;
  control?: ReactNode;
  /** The whole row body, instead of `label` and `control`. */
  children?: ReactNode;
  tooltip?: string;
  mono?: boolean;
  truncate?: boolean;
  align?: 'start' | 'center';
  /** Indents the row under the one above it. */
  nested?: boolean;
}) {
  const body = children ?? (
    <div
      title={tooltip}
      className={cn(
        'flex justify-between gap-4',
        align === 'center' ? 'items-center' : 'items-start',
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <Text>{label}</Text>
        {(description || learnMoreUrl) && (
          <Text size="sm" variant="secondary" mono={mono} truncate={truncate}>
            {description}
            {learnMoreUrl && (
              <>
                {' '}
                <LearnMoreLink link={learnMoreUrl} />
              </>
            )}
          </Text>
        )}
      </div>
      {control}
    </div>
  );

  if (!nested) return body;

  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 text-spark-text-tertiary">
        <Icon name="inherit" size="sm" />
      </span>
      <div className="min-w-0 flex-1">{body}</div>
    </div>
  );
}
